import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Input } from './ui/input';
import { discussionsAPI } from '../api';
import { toast } from 'sonner';
import { Paperclip, Library, FileText, Search, Check, X } from 'lucide-react';
import type { AttachableArticle } from '../types';

interface ArticleAttachmentPickerProps {
  selectedArticles: AttachableArticle[];
  onSelectionChange: (articles: AttachableArticle[]) => void;
  maxArticles?: number;
}

const ArticleAttachmentPicker: React.FC<ArticleAttachmentPickerProps> = ({
  selectedArticles,
  onSelectionChange,
  maxArticles = 3
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [libraryArticles, setLibraryArticles] = useState<AttachableArticle[]>([]);
  const [digestArticles, setDigestArticles] = useState<AttachableArticle[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [localSelection, setLocalSelection] = useState<AttachableArticle[]>(selectedArticles);

  useEffect(() => {
    if (open) {
      loadAttachableArticles();
      setLocalSelection(selectedArticles);
    }
  }, [open]);

  const loadAttachableArticles = async (): Promise<void> => {
    setLoading(true);
    try {
      const data = await discussionsAPI.getAttachableArticles();
      setLibraryArticles(data.library_articles);
      setDigestArticles(data.digest_articles);
    } catch (error) {
      console.error('Error loading attachable articles:', error);
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  const isSelected = (pmid: string): boolean => {
    return localSelection.some(a => a.pmid === pmid);
  };

  const toggleArticle = (article: AttachableArticle): void => {
    if (isSelected(article.pmid)) {
      setLocalSelection(localSelection.filter(a => a.pmid !== article.pmid));
    } else {
      if (localSelection.length >= maxArticles) {
        toast.error(`You can only attach up to ${maxArticles} articles`);
        return;
      }
      setLocalSelection([...localSelection, article]);
    }
  };

  const handleConfirm = (): void => {
    onSelectionChange(localSelection);
    setOpen(false);
  };

  const handleCancel = (): void => {
    setLocalSelection(selectedArticles);
    setOpen(false);
  };

  const filterArticles = (articles: AttachableArticle[]): AttachableArticle[] => {
    if (!searchTerm) return articles;
    const term = searchTerm.toLowerCase();
    return articles.filter(a => 
      a.title.toLowerCase().includes(term) || 
      (a.journal?.toLowerCase().includes(term))
    );
  };

  const renderArticleItem = (article: AttachableArticle) => (
    <button
      key={article.pmid}
      onClick={() => toggleArticle(article)}
      className={`w-full text-left p-3 rounded-lg border transition-colors ${
        isSelected(article.pmid)
          ? 'border-blue-500 bg-blue-50'
          : 'border-gray-200 hover:border-blue-200 hover:bg-gray-50'
      }`}
      data-testid={`attachable-article-${article.pmid}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 mt-0.5 ${
          isSelected(article.pmid) ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
        }`}>
          {isSelected(article.pmid) && <Check className="h-3 w-3 text-white" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="responsive-title text-sm font-medium text-gray-900 line-clamp-2">
            {article.title}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {article.journal} • {article.pub_date}
          </p>
          {article.design_tags && article.design_tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {article.design_tags.slice(0, 2).map((tag, idx) => (
                <Badge key={idx} variant="outline" className="text-xs py-0 px-1.5">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>
    </button>
  );

  const filteredLibrary = filterArticles(libraryArticles);
  const filteredDigest = filterArticles(digestArticles);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="text-gray-600"
          data-testid="attach-article-btn"
        >
          <Paperclip className="h-4 w-4 mr-1" />
          Attach Article
          {selectedArticles.length > 0 && (
            <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 flex items-center justify-center">
              {selectedArticles.length}
            </Badge>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Paperclip className="h-5 w-5 text-blue-600" />
            Attach Articles ({localSelection.length}/{maxArticles})
          </DialogTitle>
        </DialogHeader>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search articles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="article-search-input"
          />
        </div>

        {/* Selected Articles Preview */}
        {localSelection.length > 0 && (
          <div className="flex flex-wrap gap-2 py-2 border-b">
            {localSelection.map(article => (
              <Badge 
                key={article.pmid} 
                variant="secondary"
                className="flex items-center gap-1 pr-1"
              >
                <span className="responsive-title max-w-[150px] truncate text-xs">
                  {article.title}
                </span>
                <button
                  onClick={() => toggleArticle(article)}
                  className="hover:bg-gray-300 rounded p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="library" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="library" className="flex items-center gap-1">
              <Library className="h-4 w-4" />
              My Library ({filteredLibrary.length})
            </TabsTrigger>
            <TabsTrigger value="digest" className="flex items-center gap-1">
              <FileText className="h-4 w-4" />
              Recent Digest ({filteredDigest.length})
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="library" className="flex-1 overflow-y-auto mt-2">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : filteredLibrary.length > 0 ? (
              <div className="space-y-2">
                {filteredLibrary.map(renderArticleItem)}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Library className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                <p>No articles in your library</p>
                <p className="text-sm">Save articles to attach them to discussions</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="digest" className="flex-1 overflow-y-auto mt-2">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : filteredDigest.length > 0 ? (
              <div className="space-y-2">
                {filteredDigest.map(renderArticleItem)}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                <p>No articles in recent digest</p>
                <p className="text-sm">Articles from your latest digest will appear here</p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} data-testid="confirm-attach-btn">
            Attach {localSelection.length > 0 ? `(${localSelection.length})` : ''}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ArticleAttachmentPicker;
